import React from 'react';

import { endOfDay, isPast } from 'date-fns';
import styled from 'styled-components';

import A from '@adsk/alloy-react-a';
import Badge from '@adsk/alloy-react-badge';
import { displayDate } from '@adsk/alloy-react-datetime';
import Divider from '@adsk/alloy-react-divider';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import BlockRemoveButton from '../../../BlockRemoveButton';
import { colorByThreadStatus } from '../helpers';
import { useEntityLink } from '../helpers/useEntityLink';
import { Thread } from '../types';

const removeButtonClassName = 'CorrespondenceEntityBlock__remove-button';

const StyledEntityBlock = styled.div`
  position: relative;
  color: ${theme.colors.charcoal900};
  display: flex;
  padding: 14px 16px 12px 16px;
  flex-direction: column;
  justify-content: center;
  border-bottom: 1px solid ${theme.colors.charcoal100};
  ${theme.typography.bodySmall};
  &:hover .${removeButtonClassName} {
    visibility: visible;
  }
`;

type Props = {
  entity: Thread;
  projectId: string;
  onReferenceClickAnalytics?: () => void;
  onRemoveClick?: () => void;
};

export const CorrespondenceEntityBlock = ({
  entity,
  projectId,
  onReferenceClickAnalytics,
  onRemoveClick,
}: Props) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

  const correspondenceLink = useEntityLink(projectId, entity);

  return (
    <A
      onClick={onReferenceClickAnalytics}
      href={correspondenceLink}
      target="_blank"
      rel="noopener noreferrer"
    >
      <StyledEntityBlock>
        <div
          style={{
            marginBottom: '6px',
            maxWidth: '90%',
          }}
        >
          <span style={theme.typography.bodyMediumBold}>
            {entity.identifier}
          </span>
          <span style={theme.typography.bodySmall}>
            {entity.isArchived && ` (${T.translate('ARCHIVED')})`}
          </span>
        </div>

        <OverflowTooltip>
          {entity.subject?.length
            ? entity.subject
            : `${T.translate('<NO_SUBJECT>')} `}
        </OverflowTooltip>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '6px',
          }}
        >
          {entity.status && (
            <Badge
              size={Badge.SIZES.SMALL}
              variant={Badge.VARIANTS.LINE}
              color={colorByThreadStatus[entity.status as string]}
              style={{ padding: 0 }}
            >
              {T.translate((entity.status as string)?.toUpperCase())}
            </Badge>
          )}

          {entity.status && entity.dueDate && (
            <Divider
              variant={Divider.VARIANTS.VERTICAL}
              height={12}
              color={theme.colors.charcoal400}
              style={{ margin: '0 12px' }}
            />
          )}

          {entity.dueDate && (
            <div
              style={{
                ...(isPast(endOfDay(new Date(entity.dueDate)))
                  ? { color: theme.colors.red700 }
                  : {}),
              }}
            >
              {T.translate('DUE')} {displayDate(new Date(entity.dueDate))}
            </div>
          )}
        </div>

        {onRemoveClick && (
          <BlockRemoveButton
            className={removeButtonClassName}
            style={{
              backgroundColor: theme.colors.white,
            }}
            onClick={onRemoveClick}
            tooltipProps={{
              getTargetProps: () => ({
                style: {
                  visibility: 'hidden',
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                },
              }),
            }}
          />
        )}
      </StyledEntityBlock>
    </A>
  );
};
